<template>
  <div class="widget-base-input">
    <label
      v-if="label"
      :for="name"
      :data-error="!!error"
      :data-subtitle="!!subtitle"
      :data-active="isInputFocused || !!input"
      class="widget-base-input_label"
      @click="() => textarea?.focus()"
    >
      {{ label }}
    </label>
    <div class="relative" :class="{ 'mt-1': label }">
      <textarea
        ref="textarea"
        v-model="input"
        :class="twMerge(inputVariants({ variant }), String($attrs.class || ''), `${clearable ? 'pr-10' : ''}`)"
        :placeholder="placeholder"
        :name="name"
        :autofocus="autofocus"
        :readonly="readonly"
        :data-error="!!error"
        :data-label="!!label"
        v-bind="$attrs"
        @focus="() => (isInputFocused = true)"
        @blur="() => (isInputFocused = false)"
      />
      <div
        v-if="clearable && inputValue"
        class="absolute inset-y-0 right-0 my-auto flex cursor-pointer items-center pr-3"
        @click="clearInput"
      >
        <WidgetIcon :icon="themeIcons.close" height="1.5rem" class="widget-secondary-icon" />
      </div>
    </div>
    <div v-if="error" class="mt-1 grid grid-flow-col justify-start gap-2">
      <WidgetIcon
        v-if="
          !checkTheme(
            'volvo',
            'tesla',
            'aa',
            'suzuki',
            'cinch',
            'crashdaddy',
            'volkswagen',
            'bentley',
            'vwg',
            'audi',
            'samsung'
          )
        "
        :icon="themeIcons.close"
        height="1.25rem"
        width="1.25rem"
        class="mt-0.5 text-negative"
      />
      <p class="font-normal text-negative">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends string | boolean | number | undefined">
  import WidgetIcon from './WidgetIcon.vue';
  import { cva, type VariantProps } from 'class-variance-authority';
  import { twMerge } from 'tailwind-merge';
  import { themeIcons, checkTheme, getClientTheme } from 'src/utils/config';
  import { computed, MaybeRef, onMounted, ref } from 'vue';
  import { useTextareaAutosize } from '@vueuse/core';

  defineOptions({
    inheritAttrs: false,
  });

  const inputVariants = cva(
    'body1 bg-background text-fontPrimary w-full min-h-[50px] p-3 border border-backgroundSoft-600 rounded-custom block resize-none overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed focus:border-primary focus:ring-primary focus:ring-offset-0 focus:read-only:ring-0 focus-visible:shadow-none focus-visible:outline-none data-[error=true]:border-negative data-[error=true]:focus:ring-negative data-[error=true]:focus:border-negative',
    {
      variants: {
        variant: {
          default: 'focus:ring-0',
          audi: 'px-0 pt-0 min-h-[37px] border-0 border-b border-backgroundSoft focus:border-fontPrimary focus:ring-none focus:ring-0 focus:!shadow-[0px_1px_0px_0px] focus:shadow-fontPrimary data-[error=true]:focus:shadow-negative',
          samsung:
            'px-0 pt-0 min-h-[37px] border-0 border-b border-backgroundSoft focus:border-fontPrimary focus:ring-none focus:ring-0 focus:!shadow-[0px_1px_0px_0px] focus:shadow-fontPrimary data-[error=true]:focus:shadow-negative',
          volkswagen:
            'px-0 pt-0 min-h-[37px] border-0 border-b border-backgroundSoft focus:ring-0  data-[error=true]:focus:border-backgroundSoft',
          bentley:
            'px-0 pt-0 min-h-[37px] border-0 border-b border-backgroundSoft focus:ring-0  data-[error=true]:focus:border-backgroundSoft',
          vwg: 'px-0 pt-0 min-h-[37px] border-0 border-b border-backgroundSoft focus:ring-0  data-[error=true]:focus:border-backgroundSoft',
          mercedes:
            'bg-backgroundSoft text-sm px-4 py-3 focus:border-fontPrimary focus:ring-0 data-[label=true]:pb-1 data-[label=true]:pt-5',
          tesla:
            'bg-backgroundSoft text-sm px-4 py-3 border-none outline-0 focus:outline-2 focus-visible:outline-2 outline-backgroundSoft-700 focus:outline-backgroundSoft-700 focus-visible:outline-backgroundSoft-700 transition-all focus:ring-none focus:ring-0 outline-offset-0 focus:outline-offset-0 focus-visible:outline-offset-0',
          aa: 'shadow-[0px_1px_4px_0px_#00000029,0px_0px_2px_0px_#00000014] focus-within:shadow-none focus-within:bg-[#fffcf2]',
          suzuki:
            'bg-backgroundSoft text-sm px-4 py-3 ring-0 focus:ring-0 border border-fontPrimary focus:border-fontPrimary',
          cinch: 'py-3 px-4 focus:border-[#311C77] focus:ring-[#311C77]',
          bmw: 'px-4 py-3 min-h-[50px] focus:border-fontPrimary focus:ring-fontPrimary',
          toyota: 'px-4 py-3 min-h-[50px] focus:border-fontPrimary focus:ring-fontPrimary',
          mopar: 'px-4 py-3 min-h-[50px]',
          jeep: 'px-4 py-3 min-h-[50px]',
          mini: 'px-4 py-3 min-h-[50px] focus:border-fontPrimary focus:ring-fontPrimary',
          jaguar: 'p-5 min-h-[66px] focus:border-fontPrimary focus:ring-0',
          jlr: 'px-5 py-4 min-h-[58px] focus:border-fontPrimary focus:ring-0 !font-widget-primary',
          common: 'px-5 py-4 min-h-[58px] focus:border-fontPrimary focus:ring-0',
          referral: 'px-5 py-4 min-h-[58px] focus:border-fontPrimary focus:ring-0 !font-widget-primary',
          landrover: 'p-5 min-h-[66px] focus:border-fontPrimary focus:ring-0',
          volvo: 'px-5 py-4 min-h-[58px]',
          crashdaddy: 'px-5 py-4 min-h-[58px]',
        },
      },
      defaultVariants: {
        variant: getClientTheme(),
      },
    }
  );

  type InputProps = VariantProps<typeof inputVariants>;

  const props = withDefaults(
    defineProps<{
      modelValue?: T | string;
      value?: T | string;
      label?: string;
      placeholder?: string;
      name?: string;
      type?: string;
      autofocus?: boolean;
      readonly?: boolean;
      clearable?: boolean;
      error?: string;
      variant?: InputProps['variant'];
      subtitle?: boolean;
      id?: string;
    }>(),
    {
      modelValue: '',
      value: '',
      label: '',
      placeholder: '',
      name: '',
      type: 'text',
      autofocus: false,
      readonly: false,
      clearable: false,
      subtitle: false,
      error: '',
      variant: undefined,
      id: '',
    }
  );

  const emit = defineEmits<{
    (event: 'update:modelValue', value: T | string): void;
    (event: 'clear'): void;
  }>();

  const inputValue = computed({
    get: () => props.modelValue || props.value,
    set: (val) => {
      emit('update:modelValue', val);
    },
  });
  const { textarea, input } = useTextareaAutosize({ input: inputValue as MaybeRef<string> });

  const error = computed(() => props.error);
  const isInputFocused = ref(false);

  function clearInput() {
    inputValue.value = '';
    emit('clear');
  }

  onMounted(() => {
    if (props.autofocus) {
      textarea.value?.focus();
    }
  });
</script>
